import React, { forwardRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { Button } from 'components';
import ReactMultiCarousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import ReviewCard from './ReviewCard';

const GoogleReviews = forwardRef((props, ref) => {
  const { title, backgroundColor, showCount = 10 } = props;

  const data = useStaticQuery(graphql`
    query {
      allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/google-reviews/" } }
        sort: { fileAbsolutePath: ASC }
      ) {
        nodes {
          id
          html
          frontmatter {
            name
            date
            link
          }
        }
      }
    }
  `);

  // Transform markdown data to match expected review format
  const reviews = data.allMarkdownRemark.nodes.slice(0, showCount).map((node) => ({
    reviewId: node.id,
    reviewerName: node.frontmatter.name,
    reviewerPhotoUrl: null, // No photos as requested
    rating: 5, // All reviews are 5 stars
    reviewText: node.html.replace(/<p>|<\/p>/g, ''), // Remove p tags from HTML
    reviewTime: node.frontmatter.date, // Relative date string
    link: node.frontmatter.link
  }));

  const responsive = {
    desktop: {
      breakpoint: { max: 10000, min: 1200 },
      items: 3
    },
    tablet: {
      breakpoint: { max: 1199, min: 840 },
      items: 2
    },
    mobile: {
      breakpoint: { max: 839, min: 0 },
      items: 1
    }
  };

  if (!reviews.length) {
    return (
      <Container ref={ref} $backgroundColor={backgroundColor}>
        <Title>{title || 'Google Reviews'}</Title>
        <ErrorContainer>
          <ErrorText>No reviews available at this time.</ErrorText>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container ref={ref} $backgroundColor={backgroundColor}>
      <Title>{title || 'Google Reviews'}</Title>
      <CarouselContainer>
        <StyledReactMultiCarousel
          responsive={responsive}
          swipeable
          draggable
          showDots={false}
          arrows={false}
          ssr
          infinite={reviews.length > 3}
          autoPlay={reviews.length > 1}
          autoPlaySpeed={5000}
          keyBoardControl
          renderDotsOutside>
          {reviews.map((review) => (
            <ReviewCard key={review.reviewId} review={review} />
          ))}
        </StyledReactMultiCarousel>
      </CarouselContainer>
      <GoogleBadge>
        <BadgeText>Powered by</BadgeText>
        <GoogleLogo
          src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
          alt="Google"
          width="46"
          height="15"
        />
      </GoogleBadge>
      <a
        style={{ display: 'inline-block', margin: '1.6rem auto 0' }}
        href="https://www.google.com/search?sca_esv=9349b9a2334e1130&rlz=1C5CHFA_enIE1156IE1156&si=AMgyJEuzsz2NflaaWzrzdpjxXXRaJ2hfdMsbe_mSWso6src8s2hhWChoTRwKllu80q7gdr7vsWZV3pwitY_xMOxeVtJvX1aEtb8ZUBMepO3iX5fkLalpRtvD_s1228rDBhBSRSHd13hL&q=Connolly+Stairs+Reviews&sa=X&ved=2ahUKEwiEoe_lh_GPAxXPT0EAHbgJL04Q0bkNegQISBAE&biw=2327&bih=1176&dpr=1.1"
        target="_blank"
        rel="noreferrer">
        <Button>+&nbsp;&nbsp;Add Your Review</Button>
      </a>
    </Container>
  );
});

const Container = styled.section`
  background-color: ${({ $backgroundColor }) => $backgroundColor || 'var(--connolly-grey)'};
  margin-bottom: 4.5rem;
  text-align: center;
  width: 100%;
`;

const Title = styled.h3`
  color: var(--connolly-black);
  font-size: 2rem;
  margin: 0;
  padding: 1.75em 2rem 0.625em;
  text-align: center;

  @media (width >= 768px) {
    padding: 3.125em 0 1.25em;
  }
`;

const CarouselContainer = styled.div`
  margin: 0 auto;

  /* max-width: 1200px; */
  padding: 0 1rem;

  @media (width >= 768px) {
    padding: 0 2rem;
  }

  > div {
    margin: 0 auto;
    max-width: 22rem;
    position: relative;

    @media (width >= 430px) {
      max-width: 25.6rem;
    }

    @media (width >= 840px) {
      max-width: 51.25rem;
    }

    @media (width >= 1200px) {
      max-width: 71rem;
    }

    @media (width >= 1270px) {
      max-width: 76.875rem;
    }
  }
`;

const StyledReactMultiCarousel = styled(ReactMultiCarousel)`
  position: relative !important;

  @media (width >= 430px) {
    left: 1.5625rem;
  }

  .react-multi-carousel-dot-list {
    bottom: -2.125rem;
    left: 50%;
    transform: translateX(-50%);
    position: relative;
    text-align: center;
  }

  .react-multi-carousel-dot button {
    border: none;
    width: 0.625rem;
    height: 0.625rem;
    background-color: #ccc;
  }

  .react-multi-carousel-dot--active button {
    background-color: var(--connolly-red);
  }

  .react-multi-carousel-track {
    display: flex;
    align-items: stretch;
  }

  .react-multi-carousel-item {
    display: flex;
  }
`;

const ErrorContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  justify-content: center;
  padding: 4rem 2rem;
`;

const ErrorText = styled.p`
  color: var(--connolly-black);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0;
  text-align: center;
`;

const GoogleBadge = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 2rem;
  opacity: 0.7;
`;

const GoogleLogo = styled.img`
  height: auto;
  object-fit: contain;
`;

const BadgeText = styled.span`
  color: #666;
  font-size: 0.75rem;
  margin-bottom: 0.18rem;
`;

export default GoogleReviews;
